import { EmptyState, Loading, Table, TableShell, TBody, Td, Th, THead, Tr } from '../../ui/index.js';
import { getWooOrderStatus } from '@mapara/core';
import { Button } from '../../ui/index.js';
import { STATUS_PILL, STATUS_DOT, STATUS_STRIPE, money, relative } from './presentation.js';
import type { ShopOrderRow } from './types.js';

export interface OrderTableProps {
  readonly rows: readonly ShopOrderRow[];
  readonly selectedWooId: number | null;
  readonly onSelect: (wooId: number) => void;
  readonly loading: boolean;
  readonly error: string | null;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
}

function StatusPill({ status }: { readonly status: string }): React.ReactElement {
  const dot = STATUS_DOT[status] ?? 'bg-gray-400';
  const pill = STATUS_PILL[status] ?? 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {status === 'pending_call' ? 'À appeler' : status === 'unreachable' ? 'Injoignable' : status === 'callback' ? 'Rappel' : status === 'confirmed' ? 'Confirmée' : status === 'shipped' ? 'Expédiée' : status === 'cancelled' ? 'Annulée' : status}
    </span>
  );
}

export function OrderTable({
  rows,
  selectedWooId,
  onSelect,
  loading,
  error,
  page,
  pageSize,
  total,
  onPage,
}: OrderTableProps): React.ReactElement {
  const pages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  return (
    <section className="flex min-w-0 flex-1 flex-col">
      <TableShell className="min-h-0 flex-1 overflow-auto">
        <Table>
          <THead>
            <Th className="w-1" />
            <Th>Commande</Th>
            <Th>Client</Th>
            <Th>Contact</Th>
            <Th>Livraison</Th>
            <Th align="right">Total</Th>
            <Th>Statut</Th>
            <Th align="center">Essais</Th>
            <Th>Prochain appel</Th>
            <Th>Affecté</Th>
          </THead>
          <TBody>
            {rows.map((order) => {
              const selected = order.wooId === selectedWooId;
              const woo = getWooOrderStatus(order.status);
              return (
                <Tr
                  key={order.id}
                  onClick={() => onSelect(order.wooId)}
                  className={[
                    'cursor-pointer transition-colors',
                    selected ? 'bg-herb-600/10 ring-1 ring-inset ring-herb-600/40' : 'hover:bg-panel/70',
                  ].join(' ')}
                >
                  <Td className="p-0">
                    <span
                      aria-hidden="true"
                      className={`block h-full min-h-10 w-1 ${STATUS_STRIPE[order.confirmationStatus] ?? 'bg-ink-ghost'}`}
                    />
                  </Td>
                  <Td>
                    <span className="block font-mono text-sm font-semibold tabular-nums text-ink">{order.number}</span>
                    <span className="block text-[10px] text-ink-faint">{woo?.badgeLabel ?? order.status}</span>
                  </Td>
                  <Td>
                    <span className="block truncate text-sm font-medium text-ink">
                      {order.customerName ?? '—'}
                    </span>
                    {order.assignedToName !== null && (
                      <span className="block truncate text-[11px] text-ink-faint">{order.assignedToName}</span>
                    )}
                  </Td>
                  <Td className="font-mono text-sm tabular-nums text-ink-soft">
                    {order.billingPhone ?? '—'}
                    {order.billingEmail && (
                      <span className="block max-w-[11rem] truncate font-sans text-[11px] text-ink-faint">
                        {order.billingEmail}
                      </span>
                    )}
                  </Td>
                  <Td className="text-sm text-ink-soft">
                    <span className="block truncate">
                      {[order.billingCity, order.billingGov].filter((v) => v !== null && v !== '').join(' · ') || '—'}
                    </span>
                    {order.carrierName && (
                      <span className="inline-block mt-0.5 rounded bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
                        {order.carrierName}
                      </span>
                    )}
                  </Td>
                  <Td align="right" className="text-sm font-semibold tabular-nums text-ink">{money(order.total)}</Td>
                  <Td>
                    <StatusPill status={order.confirmationStatus} />
                  </Td>
                  <Td align="center" className="text-sm tabular-nums text-ink-soft">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-panel text-xs font-semibold">
                      {order.callAttempts}
                    </span>
                  </Td>
                  <Td className="text-sm text-ink-soft">
                    {order.callbackAt !== null ? (
                      <span className="text-blue-600">{relative(order.callbackAt)}</span>
                    ) : order.lastCallAt !== null ? (
                      relative(order.lastCallAt)
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td className="text-sm">
                    {order.locked && (
                      <span className="text-[10px] font-medium text-clay-600">
                        {order.lockedByMe ? '🔒 vous' : '🔒 autre'}
                      </span>
                    )}
                    {order.jetpackCode !== null && (
                      <span className="block font-mono text-[10px] text-emerald-700">{order.jetpackCode}</span>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      </TableShell>

      <footer className="flex items-center justify-between border-t border-border bg-card px-4 py-2.5">
        <div className="flex items-center gap-4">
          <p className="text-xs text-ink-faint">
            {total === 0 ? 'Aucune commande' : `${total} commande${total > 1 ? 's' : ''}`}
          </p>
          <div className="hidden items-center gap-1 text-[10px] text-ink-faint md:flex">
            <kbd className="rounded border border-border bg-panel px-1.5 py-0.5 font-mono">j</kbd>
            <kbd className="rounded border border-border bg-panel px-1.5 py-0.5 font-mono">k</kbd>
            <span>naviguer</span>
            <kbd className="rounded border border-border bg-panel px-1.5 py-0.5 font-mono">e</kbd>
            <span>modifier</span>
            <kbd className="rounded border border-border bg-panel px-1.5 py-0.5 font-mono">/</kbd>
            <span>rechercher</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            ‹ Précédent
          </Button>
          <span className="rounded-md bg-panel px-2.5 py-1 text-xs font-semibold tabular-nums text-ink-soft">
            {page} / {pages}
          </span>
          <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onPage(page + 1)}>
            Suivant ›
          </Button>
        </div>
      </footer>

      {error !== null && (
        <div className="border-t border-rust-600/30 bg-rust-600/5 px-4 py-2 text-xs text-rust-600">{error}</div>
      )}
      {loading && rows.length === 0 && <Loading label="Chargement de la file..." />}
      {!loading && rows.length === 0 && error === null && (
        <EmptyState title="File vide" hint="Aucune commande ne correspond à ces filtres." />
      )}
    </section>
  );
}